import { useEffect, useState, useCallback, useRef } from "react";
import { Outlet, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScadaTicker from "@/components/ScadaTicker";
import EditBar from "@/components/EditBar";
import { useApp } from "@/context/AppContext";
import api, { apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Loader2 } from "lucide-react";

const DEFAULT_STRIP = [
  { key: "solar", img: "https://images.unsplash.com/photo-1629726797843-618688139f5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=480", to: "/solar", alt: "Solar Plant" },
  { key: "solar2", img: "https://images.unsplash.com/photo-1770936994282-8811fb7129ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=480", to: "/solar", alt: "Solar Power Plant" },
  { key: "media", img: "https://images.unsplash.com/photo-1781127445077-c948a9dd0ca4?crop=entropy&cs=srgb&fm=jpg&q=85&w=480", to: "/media", alt: "Media Studios" },
  { key: "media2", img: "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152?crop=entropy&cs=srgb&fm=jpg&q=85&w=480", to: "/media", alt: "Film Production" },
  { key: "mining", img: "https://images.unsplash.com/photo-1523848309072-c199db53f137?crop=entropy&cs=srgb&fm=jpg&q=85&w=480", to: "/mining", alt: "Mining & Minerals" },
  { key: "mining2", img: "https://images.unsplash.com/photo-1622645636770-11fbf0611463?crop=entropy&cs=srgb&fm=jpg&q=85&w=480", to: "/mining", alt: "Mining Haul Truck" },
];

const CARD_CLS = "relative block w-28 h-16 sm:w-44 sm:h-24 rounded-xl overflow-hidden border border-amber-300/60 shadow-lg shadow-teal-900/15 ring-1 ring-teal-100 transition-all duration-300 group-hover:scale-[1.04] group-hover:-translate-y-0.5 group-hover:shadow-xl group-hover:border-amber-400";

export default function Layout() {
  const { user, editMode } = useApp();
  const canManage = user && editMode;
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    api.get("/media?category=strip").then(({ data }) => setPhotos(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      fd.append("category", "strip");
      fd.append("media_type", "photo");
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added to strip");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this strip photo?")) return;
    try { await api.delete(`/media/${id}`); setPhotos((x) => x.filter((p) => p.id !== id)); toast.success("Photo removed"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const items = photos.length > 0
    ? photos.map((p) => ({ key: p.id, img: fileUrl(p.storage_path), alt: p.title, to: null }))
    : DEFAULT_STRIP;

  const card = (v, suffix = "") => {
    const inner = (
      <span className={CARD_CLS}>
        <img src={v.img} alt={v.alt} className="w-full h-full object-cover saturate-[1.15] transition-transform duration-500 group-hover:scale-110" />
        <span className="absolute inset-0 bg-gradient-to-t from-[#014845]/35 via-transparent to-transparent pointer-events-none" aria-hidden="true" />
      </span>
    );
    return v.to
      ? <Link key={`${v.key}${suffix}`} to={v.to} data-testid={`strip-${v.key}${suffix}`} className="group shrink-0" title={v.alt}>{inner}</Link>
      : <span key={`${v.key}${suffix}`} data-testid={`strip-${v.key}${suffix}`} className="group shrink-0">{inner}</span>;
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFFFF]">
      <Header />
      <ScadaTicker />
      <div className="bg-white border-b border-amber-200/60 shadow-sm" data-testid="verticals-strip">
        {canManage ? (
          <div className="oj-container py-3 flex flex-wrap items-center justify-center gap-4 sm:gap-5">
            {items.map((v) => (
              <div key={v.key} className="group relative">
                {card(v)}
                {photos.length > 0 && (
                  <button onClick={() => remove(v.key)} data-testid={`strip-delete-${v.key}`} title="Delete photo"
                    className="absolute -top-2 -right-2 z-10 grid place-items-center w-6 h-6 rounded-full bg-red-600 text-white shadow-lg hover:bg-red-700">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="strip-add-photo" title="Upload photo to strip"
              className="w-28 h-16 sm:w-44 sm:h-24 rounded-xl border-2 border-dashed border-teal-300 grid place-items-center text-teal-600 transition-colors hover:border-amber-400 hover:text-amber-600">
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Plus className="w-7 h-7" />}
            </button>
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="strip-file-input" />
          </div>
        ) : (
          <div className="overflow-hidden" data-testid="strip-marquee">
            <div className="flex w-max items-center gap-4 sm:gap-5 py-3 animate-ticker">
              {items.map((v) => card(v, "-a"))}
              {items.map((v) => card(v, "-b"))}
            </div>
          </div>
        )}
      </div>
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <EditBar />
    </div>
  );
}
